<template>
  <section class="amtli-journey" :class="isDarkMode ? 'amtli-journey--dark' : 'amtli-journey--light'" aria-label="Fortschritt der Ämtli-Planung">
    <div class="amtli-journey__topline">
      <button type="button" class="amtli-journey__icon-button" aria-label="Zurück" @click="$emit('back')">
        <v-icon size="21" color="inherit">mdi-arrow-left</v-icon>
      </button>

      <div class="amtli-journey__identity" aria-hidden="true">
        <v-icon size="22" color="inherit">mdi-clipboard-check-outline</v-icon>
      </div>

      <div class="amtli-journey__copy">
        <div class="amtli-journey__eyebrow">
          {{ safeStep === 1 ? "ÄMTLI-KONFIGURATION" : "ÄMTLI-PLANUNG" }}
        </div>
        <h2 class="amtli-journey__title">{{ title }}</h2>
        <p v-if="subtitle" class="amtli-journey__subtitle">{{ subtitle }}</p>
      </div>

      <div v-if="$scopedSlots.actions || $slots.actions" class="amtli-journey__actions">
        <slot name="actions" />
      </div>

      <button type="button" class="amtli-journey__close" :aria-label="resolvedCloseLabel" @click="$emit('close')">
        <span v-if="closeLabel" class="amtli-journey__close-label">{{ closeLabel }}</span>
        <v-icon size="22" color="inherit">mdi-close</v-icon>
      </button>
    </div>

    <nav class="amtli-journey__desktop-steps" aria-label="Schritte der Ämtli-Planung">
      <div
        v-for="item in steps"
        :key="item.number"
        class="amtli-journey__step"
        :class="stepClass(item.number)"
        :aria-current="item.number === safeStep ? 'step' : null">
        <span class="amtli-journey__step-number">
          <v-icon v-if="item.number < safeStep" size="15" color="inherit">mdi-check</v-icon>
          <span v-else>{{ item.number }}</span>
        </span>
        <span class="amtli-journey__step-copy">
          <strong>{{ item.label }}</strong>
          <small>{{ item.description }}</small>
        </span>
        <span v-if="item.number < steps.length" class="amtli-journey__connector" aria-hidden="true"></span>
      </div>
    </nav>

    <div class="amtli-journey__compact-progress" aria-live="polite">
      <div class="amtli-journey__compact-copy">
        <span>Schritt {{ safeStep }} von {{ steps.length }}</span>
        <strong>{{ activeStep.label }}</strong>
      </div>
      <div
        class="amtli-journey__progress-track"
        role="progressbar"
        :aria-valuenow="safeStep"
        aria-valuemin="1"
        :aria-valuemax="steps.length"
        :aria-label="`Schritt ${safeStep} von ${steps.length}: ${activeStep.label}`">
        <span :style="{ width: progressWidth }"></span>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "WorkCalendarAmtliJourneyHeader",
  props: {
    step: {
      type: Number,
      default: 1,
    },
    title: {
      type: String,
      default: "Ämtli planen",
    },
    subtitle: {
      type: String,
      default: "",
    },
    isDarkMode: {
      type: Boolean,
      default: false,
    },
    closeLabel: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      steps: [
        { number: 1, label: "Vorlagen", description: "Aufgaben vorbereiten" },
        { number: 2, label: "Planung", description: "Zeitraum & Regeln" },
        { number: 3, label: "Zuweisungen", description: "Anpassen & übernehmen" },
      ],
    }
  },
  computed: {
    safeStep() {
      const parsed = Number(this.step)
      return Math.min(this.steps.length, Math.max(1, Number.isFinite(parsed) ? Math.round(parsed) : 1))
    },
    activeStep() {
      return this.steps[this.safeStep - 1]
    },
    progressWidth() {
      return `${(this.safeStep / this.steps.length) * 100}%`
    },
    resolvedCloseLabel() {
      return this.closeLabel || "Ämtli-Planung schliessen"
    },
  },
  methods: {
    stepClass(stepNumber) {
      return {
        "amtli-journey__step--done": stepNumber < this.safeStep,
        "amtli-journey__step--active": stepNumber === this.safeStep,
        "amtli-journey__step--upcoming": stepNumber > this.safeStep,
      }
    },
  },
}
</script>

<style scoped>
.amtli-journey {
  --journey-accent: #13a45a;
  --journey-accent-soft: rgba(19, 164, 90, 0.11);
  --journey-border: rgba(25, 38, 50, 0.1);
  --journey-muted: #718096;
  --journey-surface: #ffffff;
  --journey-surface-subtle: #f6f8fa;
  --journey-text: #17212b;
  width: 100%;
  color: var(--journey-text);
  background: var(--journey-surface);
  border-bottom: 1px solid var(--journey-border);
  box-shadow: 0 8px 24px rgba(20, 31, 44, 0.05);
}

.amtli-journey--dark {
  --journey-border: rgba(255, 255, 255, 0.09);
  --journey-muted: #91a0b1;
  --journey-surface: #111418;
  --journey-surface-subtle: #191d22;
  --journey-text: #f4f7fa;
}

.amtli-journey__topline {
  min-height: 72px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
}

.amtli-journey__icon-button,
.amtli-journey__close {
  min-width: 44px;
  min-height: 44px;
  padding: 0 11px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  flex: 0 0 auto;
  color: var(--journey-text);
  background: var(--journey-surface-subtle);
  border: 1px solid var(--journey-border);
  border-radius: 13px;
  cursor: pointer;
}

.amtli-journey__icon-button:focus-visible,
.amtli-journey__close:focus-visible {
  outline: 3px solid rgba(19, 164, 90, 0.28);
  outline-offset: 2px;
}

.amtli-journey__identity {
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  color: var(--journey-accent);
  background: var(--journey-accent-soft);
  border: 1px solid rgba(19, 164, 90, 0.2);
  border-radius: 14px;
}

.amtli-journey__copy {
  min-width: 0;
  flex: 1 1 auto;
}

.amtli-journey__eyebrow {
  margin-bottom: 2px;
  color: var(--journey-accent);
  font-size: 10px;
  font-weight: 800;
  line-height: 1.2;
  letter-spacing: 0.12em;
}

.amtli-journey__title {
  margin: 0;
  overflow: hidden;
  color: inherit;
  font-size: 20px;
  font-weight: 800;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.amtli-journey__subtitle {
  margin: 3px 0 0;
  overflow: hidden;
  color: var(--journey-muted);
  font-size: 12px;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.amtli-journey__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  flex: 0 1 auto;
}

.amtli-journey__actions >>> .v-btn {
  min-height: 44px;
}

.amtli-journey__close-label {
  font-size: 12px;
  font-weight: 700;
}

.amtli-journey__desktop-steps {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  padding: 0 20px 15px 88px;
}

.amtli-journey__step {
  min-width: 0;
  display: flex;
  align-items: center;
  position: relative;
  color: var(--journey-muted);
}

.amtli-journey__step-number {
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 2;
  flex: 0 0 auto;
  color: inherit;
  background: var(--journey-surface-subtle);
  border: 1px solid var(--journey-border);
  border-radius: 10px;
  font-size: 12px;
  font-weight: 800;
}

.amtli-journey__step-copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 2;
  padding: 0 10px;
  background: var(--journey-surface);
}

.amtli-journey__step-copy strong,
.amtli-journey__step-copy small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.amtli-journey__step-copy strong {
  font-size: 12px;
  line-height: 1.25;
}

.amtli-journey__step-copy small {
  margin-top: 2px;
  color: var(--journey-muted);
  font-size: 10px;
  line-height: 1.2;
}

.amtli-journey__connector {
  height: 1px;
  position: absolute;
  z-index: 1;
  right: 0;
  left: 30px;
  background: var(--journey-border);
}

.amtli-journey__step--done,
.amtli-journey__step--active {
  color: var(--journey-accent);
}

.amtli-journey__step--done .amtli-journey__step-number,
.amtli-journey__step--active .amtli-journey__step-number {
  background: var(--journey-accent-soft);
  border-color: rgba(19, 164, 90, 0.34);
}

.amtli-journey__step--active .amtli-journey__step-number {
  color: #ffffff;
  background: var(--journey-accent);
  box-shadow: 0 6px 16px rgba(19, 164, 90, 0.23);
}

.amtli-journey__step--active .amtli-journey__step-copy strong {
  color: var(--journey-text);
}

.amtli-journey__step--done .amtli-journey__connector {
  background: rgba(19, 164, 90, 0.42);
}

.amtli-journey__compact-progress {
  display: none;
}

@media (max-width: 1100px) {
  .amtli-journey__desktop-steps {
    display: none;
  }

  .amtli-journey__compact-progress {
    display: block;
    padding: 0 20px 14px 76px;
  }

  .amtli-journey__compact-copy {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 7px;
    color: var(--journey-muted);
    font-size: 11px;
  }

  .amtli-journey__compact-copy strong {
    overflow: hidden;
    color: var(--journey-text);
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .amtli-journey__progress-track {
    height: 5px;
    overflow: hidden;
    background: var(--journey-surface-subtle);
    border-radius: 999px;
  }

  .amtli-journey__progress-track span {
    height: 100%;
    display: block;
    background: linear-gradient(90deg, #0a944d, #25bd70);
    border-radius: inherit;
    transition: width 220ms ease;
  }

  .amtli-journey__close-label {
    display: none;
  }
}

@media (hover: none) and (pointer: coarse) {
  .amtli-journey__actions >>> .v-btn,
  .amtli-journey__icon-button,
  .amtli-journey__close {
    min-height: 44px;
  }
}

@media (max-width: 720px) {
  .amtli-journey__topline {
    min-height: 66px;
    flex-wrap: wrap;
    gap: 8px;
    padding: 10px 12px;
  }

  .amtli-journey__identity {
    display: none;
  }

  .amtli-journey__title {
    font-size: 17px;
  }

  .amtli-journey__subtitle {
    max-width: 54vw;
  }

  .amtli-journey__actions {
    width: 100%;
    justify-content: flex-end;
    order: 5;
    padding-left: 52px;
  }

  .amtli-journey__actions >>> .v-btn {
    height: auto !important;
    min-height: 44px;
    flex: 1 1 220px;
    margin: 0 !important;
    white-space: normal;
  }

  .amtli-journey__close-label {
    display: none;
  }

  .amtli-journey__compact-progress {
    padding: 0 12px 12px 64px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .amtli-journey__progress-track span {
    transition: none;
  }
}
</style>
